<template>
  <div class="cw-email-action">
    <div class="hero is-small has-text-centered">
      <div class="hero-body">
        <div v-if="loading" class="has-text-grey-medium">
          <icon name="spinner" scale="4" spin></icon>
        </div>
        <div v-else-if="success" class="has-text-success">
          <icon name="check-circle" scale="4"></icon>
        </div>
        <div v-else-if="error" class="has-text-danger">
          <icon name="exclamation-triangle" scale="4"></icon>
        </div>
        <h1 class="title is-size-4">{{ title }}</h1>
        <h3 class="subtitle is-size-5">{{ message }}</h3>
      </div>
    </div>

    <section class="section">
      <!-- Email Verification Success -->
      <div
        v-if="mode === 'verifyEmail' && success"
        class="content has-text-centered"
      >
        <p>Your email has been successfully verified!</p>
        <router-link
          to="/login"
          class="button is-medium is-primary is-outlined is-rounded"
        >
          Continue to Login
        </router-link>
      </div>

      <!-- MFA Unenrollment -->
      <div
        v-else-if="mode === 'revertSecondFactorAddition'"
        class="content has-text-centered"
      >
        <div class="notification is-light">
          <p class="mb-4">
            <strong class="has-text-danger">Important:</strong> This action will
            remove two-factor authentication from your account. You will be
            required to reconfigure it the next time you log in.
          </p>
          <p class="mb-4 has-text-danger">
            If you did not request this change, please contact support
            immediately.
          </p>
        </div>

        <div v-if="!mfaUnenrolled" class="field is-grouped is-grouped-centered">
          <div class="control">
            <button
              class="button is-medium is-danger is-outlined is-rounded"
              @click="confirmMfaUnenrollment"
              :disabled="processing"
            >
              {{ processing ? 'Processing...' : 'Remove' }}
            </button>
          </div>
          <div class="control">
            <router-link
              to="/support"
              class="button is-medium is-black is-outlined is-rounded"
            >
              Contact Support
            </router-link>
          </div>
        </div>

        <div v-else class="content">
          <p class="has-text-success">
            <icon name="check-circle"></icon>
            Two-factor authentication has been removed from your account.
          </p>
          <p>You will need to set it up again on your next login.</p>
          <router-link
            to="/login"
            class="button is-medium is-primary is-outlined is-rounded"
          >
            Go to Login
          </router-link>
        </div>
      </div>

      <!-- Email Recovery -->
      <div
        v-else-if="mode === 'recoverEmail' && success"
        class="content has-text-centered"
      >
        <p>Your email address has been restored.</p>
        <p class="mb-4">
          For security, we recommend resetting your password.
        </p>
        <router-link
          to="/login"
          class="button is-medium is-primary is-outlined is-rounded"
        >
          Continue to Login
        </router-link>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="content has-text-centered">
        <p>{{ errorMessage }}</p>
        <router-link
          to="/login"
          class="button is-medium is-black is-outlined is-rounded"
        >
          Return to Login
        </router-link>
      </div>

      <!-- Password Reset Redirect -->
      <div
        v-else-if="mode === 'resetPassword'"
        class="content has-text-centered"
      >
        <p>Redirecting to password reset...</p>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

export default {
  name: 'cw-email-action',
  data() {
    return {
      mode: null,
      oobCode: null,
      apiKey: null,
      continueUrl: null,
      lang: null,
      loading: true,
      success: false,
      error: false,
      errorMessage: '',
      title: 'Processing...',
      message: 'Please wait while we process your request.',
      processing: false,
      mfaUnenrolled: false,
    };
  },
  async mounted() {
    console.log('EmailAction component mounted!');
    console.log('Current route:', this.$route);

    // Parse query parameters
    this.mode = this.$route.query.mode;
    this.oobCode = this.$route.query.oobCode;
    this.apiKey = this.$route.query.apiKey;
    this.continueUrl = this.$route.query.continueUrl;
    this.lang = this.$route.query.lang;

    // Validate required parameters
    if (!this.mode || !this.oobCode) {
      this.handleError('Invalid or missing parameters.');
      return;
    }

    // Handle different action modes
    switch (this.mode) {
      case 'verifyEmail':
        await this.handleVerifyEmail();
        break;
      case 'resetPassword':
        this.handleResetPassword();
        break;
      case 'recoverEmail':
        await this.handleRecoverEmail();
        break;
      case 'revertSecondFactorAddition':
        this.handleMfaUnenrollment();
        break;
      default:
        this.handleError('Unknown action type.');
    }
  },
  methods: {
    async handleVerifyEmail() {
      this.title = 'Verifying Email';
      this.message = 'Please wait while we verify your email address...';

      try {
        // Apply the verification code
        await firebase.auth().applyActionCode(this.oobCode);

        this.loading = false;
        this.success = true;
        this.title = 'Email Verified';
        this.message = 'Your email has been successfully verified!';
      } catch (error) {
        window.console.error('Email verification error:', error);
        this.handleError(this.getErrorMessage(error));
      }
    },

    handleResetPassword() {
      // Redirect to existing password reset flow
      this.loading = false;
      this.title = 'Password Reset';
      this.message = 'Redirecting...';

      // Redirect to the reset password page with the oobCode
      this.$router.push({
        name: 'ResetPassword',
        query: { oobCode: this.oobCode },
      });
    },

    async handleRecoverEmail() {
      this.title = 'Recovering Email';
      this.message = 'Please wait while we recover your email address...';

      try {
        // Check the action code to get the restored email
        const info = await firebase.auth().checkActionCode(this.oobCode);
        const restoredEmail = info.data.email;

        // Apply the action code to revert the email change
        await firebase.auth().applyActionCode(this.oobCode);

        this.loading = false;
        this.success = true;
        this.title = 'Email Recovered';
        this.message = `Your email has been restored to ${restoredEmail}`;
      } catch (error) {
        window.console.error('Email recovery error:', error);
        this.handleError(this.getErrorMessage(error));
      }
    },

    handleMfaUnenrollment() {
      // Don't automatically process - require user confirmation
      this.loading = false;
      this.title = 'Remove Two-Factor Authentication';
      this.message =
        'Please confirm you want to remove two-factor authentication from your account.';
    },

    async confirmMfaUnenrollment() {
      this.processing = true;

      try {
        // Apply the action code to remove MFA
        await firebase.auth().applyActionCode(this.oobCode);

        this.processing = false;
        this.mfaUnenrolled = true;
        this.success = true;
        this.title = 'Two-Factor Authentication Removed';
        this.message =
          'Your account no longer has two-factor authentication enabled.';
      } catch (error) {
        window.console.error('MFA unenrollment error:', error);
        this.processing = false;
        this.handleError(this.getErrorMessage(error));
      }
    },

    handleError(message) {
      this.loading = false;
      this.error = true;
      this.success = false;
      this.title = 'Action Failed';
      this.message = 'We were unable to process your request.';
      this.errorMessage = message;
    },

    getErrorMessage(error) {
      switch (error.code) {
        case 'auth/expired-action-code':
          return 'This link has expired. Please request a new one.';
        case 'auth/invalid-action-code':
          return 'This link is invalid or has already been used.';
        case 'auth/user-disabled':
          return 'This user account has been disabled.';
        case 'auth/user-not-found':
          return 'No user account found with this email address.';
        default:
          return (
            error.message || 'An unexpected error occurred. Please try again.'
          );
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.cw-email-action
  max-width: 600px
  margin: 0 auto
  padding: 2rem

.notification
  text-align: left

.buttons
  margin-top: 1.5rem
</style>
